/* tslint:disable:max-file-line-count */

import * as React from 'react';

import { ILocalization, LocalizedText } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { PropertyInfoPolicyViewModel, PropertyInfoSectionViewModel, PropertyInfoSubSectionViewModel } from 'models';

import { UitkHeading } from 'uitk-react-text';

import { LazyImage } from 'components/common/lazy/lazy-image';
import { PropertyPoliciesSection } from './/property-policies-section';
import { RenderAmenityBody } from './render-amenity-helper';

interface PropertyPoliciesProps {
  l10n: ILocalization;
  policies?: PropertyInfoPolicyViewModel;
  petsPolicy?: PropertyInfoSubSectionViewModel;
  renovationsAndClosuresSection?: PropertyInfoSectionViewModel;
}

export const PropertyPolicies: React.FunctionComponent<PropertyPoliciesProps> = ({
  l10n,
  petsPolicy,
  policies,
  renovationsAndClosuresSection
}) => {
  if (!policies) {
    return null;
  }

  const info = policies && policies.info;
  const paymentOptions = info && info.paymentOptions;
  const childBeds = info && info.childBeds;
  const checkinInstructions = info && info.checkinInstructions;
  const checkinPolicies = info && info.checkinPolicies;
  const checkoutPolicies = info && info.checkoutPolicies;
  const accessMethodPolicies = info && info.accessMethodPolicies;
  const secondaryTitleClassNames = 'policies__header--secondary uitk-type-heading-500 uitk-type-bold';
  const leftColumnClasses = [
    'uitk-cell',
    's-cell-1-1',
    'l-cell-1-3',
    'xl-cell-1-3',
    's-t-padding-three',
    'm-t-padding-three',
    'l-y-padding-six',
    'l-x-padding-three',
    'xl-y-padding-six',
    'xl-x-padding-three'
  ].join(' ');

  return (
    <>
      <div className={leftColumnClasses}>
        <UitkHeading level={2} size={600}>
          {policies.title}
        </UitkHeading>
      </div>
      <div className="uitk-cell l-cell-2-3 xl-cell-2-3 l-x-padding-three xl-x-padding-three all-y-padding-three">
        <CheckInCheckOut
          checkinPolicies={checkinPolicies}
          checkoutPolicies={checkoutPolicies}
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <CheckInInstructions checkinInstructions={checkinInstructions} secondaryTitleClassNames={secondaryTitleClassNames} />
        <PropertyPoliciesSection
          id="access-methods"
          model={accessMethodPolicies}
          titleLocToken="hotels.infosite.policies.accessMethods.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <RenovationsAndClosures
          renovationsAndClosuresSection={renovationsAndClosuresSection}
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <PetsPolicy petsPolicy={petsPolicy} secondaryTitleClassNames={secondaryTitleClassNames} />
        <PropertyPoliciesSection
          id="child-beds"
          model={childBeds}
          titleLocToken="hotels.infosite.policies.childrenExtraBeds.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <PaymentOptions paymentOptions={paymentOptions} secondaryTitleClassNames={secondaryTitleClassNames} l10n={l10n} />
      </div>
    </>
  );
};

export const CheckInCheckOut = ({ checkinPolicies, checkoutPolicies, secondaryTitleClassNames }) => (
  <Viewport>
    <ViewSmall>
      <>
        <PropertyPoliciesSection
          id="property-policies-check-in"
          model={checkinPolicies}
          titleLocToken="hotels.infosite.policies.checkIn.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <PropertyPoliciesSection
          id="property-policies-check-out"
          model={checkoutPolicies}
          titleLocToken="hotels.infosite.policies.checkOut.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
      </>
    </ViewSmall>
    <ViewLarge>
      <div className="uitk-grid">
        <div className="uitk-cell all-cell-1-2">
          <PropertyPoliciesSection
            id="property-policies-check-in"
            model={checkinPolicies}
            titleLocToken="hotels.infosite.policies.checkIn.title"
            secondaryTitleClassNames={secondaryTitleClassNames}
          />
        </div>
        <div className="uitk-cell all-cell-1-2">
          <PropertyPoliciesSection
            id="property-policies-check-out"
            model={checkoutPolicies}
            titleLocToken="hotels.infosite.policies.checkOut.title"
            secondaryTitleClassNames={secondaryTitleClassNames}
          />
        </div>
      </div>
    </ViewLarge>
  </Viewport>
);

export const CheckInInstructions = ({ checkinInstructions, secondaryTitleClassNames }) => {
  if (!(checkinInstructions && checkinInstructions.lines && checkinInstructions.lines.length > 0)) {
    return null;
  }

  return (
    <div className="all-y-padding-three">
      <h3 className={secondaryTitleClassNames}>
        <LocalizedText message="hotels.infosite.policies.specialCheckInInstructions.title" />
      </h3>
      <ul className="uitk-type-list-300 all-t-margin-two">
        {checkinInstructions.lines.map((text, index) => (
          <li key={index}>{text}</li>
        ))}
      </ul>
    </div>
  );
};

export const RenovationsAndClosures = ({ renovationsAndClosuresSection, secondaryTitleClassNames }) => {
  if (!(renovationsAndClosuresSection && renovationsAndClosuresSection.title && renovationsAndClosuresSection.formattedText)) {
    return null;
  }

  return (
    <div className="all-t-padding-three">
      <h3 data-stid="section-renovations-and-closures" className={secondaryTitleClassNames}>
        {renovationsAndClosuresSection.title}
      </h3>
      <div
        data-stid="renovations-and-closures-text"
        className="uitk-type-list-300 all-y-padding-three"
        dangerouslySetInnerHTML={{ __html: renovationsAndClosuresSection.formattedText.description }}
      />
    </div>
  );
};

export const PetsPolicy = ({ petsPolicy, secondaryTitleClassNames }) => {
  if (!(petsPolicy && petsPolicy.title && petsPolicy.body && petsPolicy.body.length > 0)) {
    return null;
  }

  return (
    <div className="pets-policy all-y-padding-three">
      <h3 className={`pets-policy__title ${secondaryTitleClassNames}`}>{petsPolicy.title}</h3>
      {petsPolicy.body.map((text, index) => (
        <RenderAmenityBody text={text} index={index} category="pets-policy__details" />
      ))}
    </div>
  );
};

export const PaymentOptions = ({ paymentOptions, secondaryTitleClassNames, l10n }) => {
  if (!(paymentOptions && paymentOptions.length > 0)) {
    return null;
  }

  return (
    <div className="payment-types all-t-padding-three">
      <h3 className={`payment-types__title ${secondaryTitleClassNames}`}>
        <LocalizedText message="hotels.infosite.policies.paymentTypes.title" />
      </h3>
      <div className="payment-types__cards">
        {paymentOptions.map((type, index) => {
          const name = type.name;

          return (
            <LazyImage
              key={`payment-type-${name || index}`}
              altPlaceholderText={name ? name : l10n.formatText('hotels.infosite.policies.paymentType.altText')}
            >
              <img src={type.imageUrl} alt={name ? name : l10n.formatText('hotels.infosite.policies.paymentType.altText')} />
            </LazyImage>
          );
        })}
      </div>
    </div>
  );
};
